import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AccountingService } from '../../../services/accounting.service';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-accounting-accounts',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    ButtonModule,
    TagModule,
    DialogModule,
    FormsModule,
    InputTextModule,
    SelectModule,
    ToastModule
  ],
  providers: [MessageService],
  template: `
    <div class="p-4">
      <div class="flex justify-content-between align-items-center mb-4">
        <h1 class="text-3xl font-bold m-0 text-primary">Chart of Accounts</h1>
        <p-button label="Add Account" icon="pi pi-plus" (onClick)="showDialog()"></p-button>
      </div>

      <div class="grid">
        <div class="col-12 md:col-6 lg:col-4 p-2" *ngFor="let acc of accounts">
          <p-card styleClass="h-full shadow-2 border-round-xl overflow-hidden hover:shadow-4 transition-all transition-duration-300">
            <ng-template pTemplate="header">
               <div class="p-3 bg-primary- reverse border-bottom-1 border-300 flex justify-content-between align-items-center" [style]="{background: 'rgba(var(--primary-color-rgb), 0.05)'}">
                  <span class="font-bold text-lg">{{ acc.name }}</span>
                  <p-tag [value]="acc.type | uppercase" [severity]="getTypeSeverity(acc.type)"></p-tag>
               </div>
            </ng-template>
            <div class="py-3 px-1">
              <div class="text-400 text-sm mb-1">Current Balance</div>
              <div class="text-3xl font-bold" [class.text-red-500]="isBalanceNegative(acc)">
                {{ (acc.current_balance || 0) | currency:acc.currency }}
              </div>
              <div class="flex align-items-center mt-3 text-500 text-xs">
                <i class="pi pi-tag mr-1"></i>
                <span>{{ acc.sub_type | titlecase }}</span>
              </div>
            </div>
            <ng-template pTemplate="footer">
              <div class="flex gap-2 border-top-1 border-100 pt-3">
                <p-button label="Reconcile" icon="pi pi-check-circle" [text]="true" size="small" severity="success"></p-button>
                <p-button label="Edit" icon="pi pi-pencil" [text]="true" severity="secondary" size="small" (onClick)="showDialog(acc)"></p-button>
              </div>
            </ng-template>
          </p-card>
        </div>
      </div>

      <!-- Account Edit/Create Dialog -->
      <p-dialog [(visible)]="displayDialog" [modal]="true" [header]="isEditing ? 'Edit Account' : 'New Account'" [style]="{width: '450px'}" styleClass="p-fluid">
        <div class="flex flex-column gap-4 p-2">
          <div class="field">
            <label for="name" class="font-bold block mb-2">Account Name</label>
            <input pInputText id="name" [(ngModel)]="selectedAccount.name" placeholder="e.g. Operating Expenses" autofocus />
          </div>

          <div class="field">
            <label for="type" class="font-bold block mb-2">Account Type</label>
            <p-select [options]="accountTypes" [(ngModel)]="selectedAccount.type" optionLabel="label" optionValue="value" placeholder="Select type" appendTo="body"></p-select>
          </div>

          <div class="field">
            <label for="sub_type" class="font-bold block mb-2">Sub-type</label>
            <p-select [options]="subTypes" [(ngModel)]="selectedAccount.sub_type" optionLabel="label" optionValue="value" placeholder="Select sub-type" appendTo="body"></p-select>
          </div>

          <div class="field">
            <label for="currency" class="font-bold block mb-2">Currency</label>
            <input pInputText id="currency" [(ngModel)]="selectedAccount.currency" maxlength="3" />
          </div>

          <div *ngIf="!isEditing" class="field">
            <label for="balance" class="font-bold block mb-2">Opening Balance</label>
            <input pInputText type="number" id="balance" [(ngModel)]="selectedAccount.current_balance" />
          </div>
        </div>
        
        <ng-template pTemplate="footer">
          <p-button label="Cancel" icon="pi pi-times" [text]="true" (onClick)="displayDialog = false"></p-button>
          <p-button [label]="isEditing ? 'Update Account' : 'Create Account'" icon="pi pi-check" (onClick)="saveAccount()" [disabled]="!selectedAccount.name || !selectedAccount.type"></p-button>
        </ng-template>
      </p-dialog>
    </div>
    <p-toast></p-toast>
  `
})
export class AccountsComponent implements OnInit {
  accounts: any[] = [];
  displayDialog = false;
  isEditing = false;
  selectedAccount: any = {};

  accountTypes = [
    { label: 'Asset', value: 'asset' },
    { label: 'Liability', value: 'liability' },
    { label: 'Equity', value: 'equity' },
    { label: 'Revenue', value: 'revenue' },
    { label: 'Expense', value: 'expense' }
  ];

  subTypes = [
    { label: 'Bank', value: 'bank' },
    { label: 'Cash', value: 'cash' },
    { label: 'Credit Card', value: 'credit_card' },
    { label: 'Accounts Receivable', value: 'accounts_receivable' },
    { label: 'Accounts Payable', value: 'accounts_payable' },
    { label: 'Income', value: 'income' },
    { label: 'Other Expense', value: 'expense' }
  ];

  constructor(
    private accountingService: AccountingService,
    private messageService: MessageService
  ) { }

  ngOnInit() {
    this.loadAccounts();
  }

  loadAccounts() {
    this.accountingService.getAccounts().subscribe(data => this.accounts = data);
  }

  showDialog(account?: any) {
    if (account) {
      this.isEditing = true;
      this.selectedAccount = { ...account };
    } else {
      this.isEditing = false;
      this.selectedAccount = {
        name: '',
        type: 'asset',
        sub_type: 'bank',
        currency: 'USD',
        current_balance: 0
      };
    }
    this.displayDialog = true;
  }

  saveAccount() {
    if (this.isEditing) {
      this.accountingService.updateAccount(this.selectedAccount.id, this.selectedAccount).subscribe({
        next: () => {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Account updated successfully' });
          this.displayDialog = false;
          this.loadAccounts();
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update account' });
        }
      });
    } else {
      this.accountingService.createAccount(this.selectedAccount).subscribe({
        next: () => {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Account created successfully' });
          this.displayDialog = false;
          this.loadAccounts();
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to create account' });
        }
      });
    }
  }

  getTypeSeverity(type: string): "info" | "success" | "warn" | "danger" | "secondary" | "contrast" | undefined {
    switch (type.toLowerCase()) {
      case 'asset': return 'info';
      case 'revenue': return 'success';
      case 'liability': return 'warn';
      case 'expense': return 'danger';
      default: return 'secondary';
    }
  }

  isBalanceNegative(acc: any): boolean {
    if (!acc.current_balance) return false;
    // For liabilities and revenue, a mathematical negative might actually be positive in accounting terms, 
    // but for the UI we usually just color absolute values or specific conditions.
    // Let's keep it simple for now.
    return acc.current_balance < 0;
  }
}
